import React from 'react';
import {
  Control,
  Controller,
  FieldValues,
  get,
  Path,
  PathValue,
  useFormState,
} from 'react-hook-form';
import clsx from 'clsx';
import ControllerInput from '../ui/ControllerInput';
import { usePastedField } from '@/contexts/PastedFieldsContext';

interface StyledInputProps<FieldsType extends FieldValues> {
  title?: string;
  titleClassName?: string;
  titleIcon?: React.ReactNode;
  name: Path<FieldsType>;
  control: Control<FieldsType>;
  type?: React.HTMLInputTypeAttribute;
  defaultValue?: PathValue<FieldsType, Path<FieldsType>>;
  className?: React.HTMLAttributes<HTMLDivElement>['className'];
  placeholder?: string;
  disabled?: boolean;
  icon?: React.ReactNode;
  iconPosition?: 'left' | 'right';
  min?: number;
  max?: number;
  inputMode?: React.HTMLAttributes<HTMLInputElement>['inputMode'];
  hideNumberFieldArrows?: boolean;
  onChange?: (event: React.ChangeEvent<HTMLInputElement>) => void;
  compareValue?: {
    value?: number | string;
    color: 'success' | 'warning' | 'purple';
  };
}

const StyledInput = <FieldsType extends FieldValues>({
  title,
  control,
  titleClassName,
  titleIcon,
  name,
  type = 'text',
  defaultValue,
  className,
  placeholder,
  disabled = false,
  icon,
  iconPosition = 'left',
  min,
  max,
  inputMode,
  hideNumberFieldArrows = false,
  onChange,
  compareValue,
}: StyledInputProps<FieldsType>) => {
  const { errors } = useFormState({ control });
  const errorMessage = get(errors, name + '.message') as unknown as string;
  const { isPasted, unmark } = usePastedField(name);

  return (
    <div className={clsx('flex flex-col w-full gap-1.5', className)}>
      <Controller
        control={control}
        name={name}
        render={({ field }) => (
          <ControllerInput
            field={field}
            title={title}
            errorMessage={errorMessage}
            titleClassName={titleClassName}
            titleIcon={titleIcon}
            name={name}
            placeholder={placeholder}
            type={type}
            disabled={disabled}
            icon={icon}
            iconPosition={iconPosition}
            min={min}
            max={max}
            inputMode={inputMode}
            isPasted={isPasted}
            onChange={(event) => {
              if (isPasted) unmark();
              const returnValue = field.onChange(event);
              if (onChange) {
                onChange(event);
              }
              return returnValue;
            }}
            hideNumberFieldArrows={hideNumberFieldArrows}
            compareValue={compareValue}
          />
        )}
        defaultValue={defaultValue}
      />
      {errorMessage !== '' && (
        <p className="text-sm leading-6 font-medium text-error-500">
          {errorMessage}
        </p>
      )}
    </div>
  );
};

export default StyledInput;
